/**
 * Shared ActionItemCard component for rendering unified action items.
 *
 * Used by both the Action Items page and the domain Enforcement tab.
 */

import { useState, useRef, useEffect } from "react";
import {
  CheckCircle2,
  Lightbulb,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Clock,
  CheckCircle,
  Ban,
} from "lucide-react";
import {
  type UnifiedItem,
  BORDER_COLORS,
  SEVERITY_BADGE,
  SOURCE_BADGE,
} from "./shared";

// ============================================================================
// Confidence Badge
// ============================================================================

const CONFIDENCE_CONFIG = {
  high: { className: "bg-emerald-100 text-emerald-700", tooltip: "Verified by DNS records and/or high message volume" },
  medium: { className: "bg-amber-100 text-amber-700", tooltip: "Based on available data — verify before acting" },
  low: { className: "bg-slate-100 text-slate-600", tooltip: "Limited data — needs investigation" },
};

export function ConfidenceBadge({ confidence }: { confidence: "high" | "medium" | "low" }) {
  const config = CONFIDENCE_CONFIG[confidence];
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium ${config.className} cursor-help`}
      title={config.tooltip}
    >
      {confidence} confidence
    </span>
  );
}

// ============================================================================
// Insight Details — auth rates + correlated domain
// ============================================================================

export function InsightDetails({ metadata }: { metadata: Record<string, unknown> }) {
  const spfAuth = typeof metadata.spfAuthPassRate === "number" ? metadata.spfAuthPassRate : null;
  const spfAlign = typeof metadata.spfPassRate === "number" ? metadata.spfPassRate : null;
  const dkimAuth = typeof metadata.dkimAuthPassRate === "number" ? metadata.dkimAuthPassRate : null;
  const dkimAlign = typeof metadata.dkimPassRate === "number" ? metadata.dkimPassRate : null;
  const dkimResult = typeof metadata.dkimAuthResult === "string" ? metadata.dkimAuthResult : null;
  const correlatedDomain = typeof metadata.correlatedDomain === "string" ? metadata.correlatedDomain : null;
  const correlationReason = typeof metadata.correlationReason === "string" ? metadata.correlationReason : null;

  const hasAuthRates = spfAuth != null || dkimAuth != null;
  if (!hasAuthRates && !correlatedDomain) return null;

  return (
    <>
      {hasAuthRates && (
        <div className="flex gap-3 flex-wrap">
          {spfAuth != null && (
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 min-w-[180px]">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">SPF</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Authentication:</span>
                  <span className="font-medium text-slate-800">{spfAuth}%</span>
                </div>
                {spfAlign != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Alignment:</span>
                    <span className="font-medium text-slate-800">{spfAlign}%</span>
                  </div>
                )}
                {spfAuth > 50 && spfAlign != null && spfAlign < 50 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Auth passes but alignment fails — envelope-from domain mismatch
                  </p>
                )}
              </div>
            </div>
          )}
          {dkimAuth != null && (
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 min-w-[180px]">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">DKIM</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Authentication:</span>
                  <span className="font-medium text-slate-800">{dkimAuth}%</span>
                </div>
                {dkimAlign != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Alignment:</span>
                    <span className="font-medium text-slate-800">{dkimAlign}%</span>
                  </div>
                )}
                {dkimResult && (
                  <p className="text-xs text-slate-500 mt-1">Result: {dkimResult}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {correlatedDomain && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <span className="font-medium">Likely sending domain:</span>
            <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{correlatedDomain}</code>
          </div>
          {correlationReason && (
            <p className="text-xs text-blue-600 mt-1">{correlationReason}</p>
          )}
        </div>
      )}
    </>
  );
}

// ============================================================================
// Insight overflow menu (Snooze / Mark Resolved / Not Applicable)
// ============================================================================

function InsightMenu({
  insightId,
  onSnooze,
  onResolve,
  onSuppress,
}: {
  insightId: string;
  onSnooze: (id: string) => void;
  onResolve: (id: string) => void;
  onSuppress: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        title="Insight actions"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-20">
          <button
            onClick={() => { onSnooze(insightId); setOpen(false); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-t-lg"
          >
            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
            Snooze 30 days
          </button>
          <button
            onClick={() => { onResolve(insightId); setOpen(false); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            Mark Resolved
          </button>
          <button
            onClick={() => { onSuppress(insightId); setOpen(false); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-b-lg"
          >
            <Ban className="w-4 h-4 text-slate-400 flex-shrink-0" />
            Not Applicable
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Action Item Card
// ============================================================================

export function ActionItemCard({
  item,
  id,
  highlighted,
  onNavigate,
  onResolveAlert,
  onCompleteTask,
  onSkipTask,
  onSnoozeInsight,
  onResolveInsight,
  onSuppressInsight,
}: {
  item: UnifiedItem;
  id?: string;
  highlighted?: boolean;
  onNavigate: (path: string) => void;
  onResolveAlert?: (alertId: string) => void;
  onCompleteTask?: (taskId: string) => void;
  onSkipTask?: (taskId: string) => void;
  onSnoozeInsight?: (insightId: string) => void;
  onResolveInsight?: (insightId: string) => void;
  onSuppressInsight?: (insightId: string) => void;
}) {
  const [expanded, setExpanded] = useState(item.priority === "critical");

  const hasInsightMenu =
    item.rawInsight && onSnoozeInsight && onResolveInsight && onSuppressInsight;

  return (
    <div
      id={id}
      className={`bg-white rounded-xl border border-slate-200 border-l-4 ${BORDER_COLORS[item.priority] || BORDER_COLORS.info}${highlighted ? " ring-2 ring-amber-400 shadow-md transition-all duration-300" : ""}`}
    >
      <div className="p-3 sm:p-4">
        {/* Header row */}
        <div className="flex items-start gap-2 sm:gap-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-0.5 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
              <h3
                className="text-sm font-semibold text-slate-800 cursor-pointer hover:text-purple-700"
                onClick={() => setExpanded(!expanded)}
              >
                {item.title}
              </h3>
              <span className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_BADGE[item.priority] || SEVERITY_BADGE.info}`}>
                {item.priority}
              </span>
              <span className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[item.source].className}`}>
                {SOURCE_BADGE[item.source].label}
              </span>
              {item.category && (
                <span className="hidden sm:inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
                  {item.category}
                </span>
              )}
              {item.confidence && item.source === "insight" && (
                <span className="hidden sm:inline-block">
                  <ConfidenceBadge confidence={item.confidence} />
                </span>
              )}
            </div>
            <p className={`text-sm text-slate-600 ${expanded ? '' : 'line-clamp-2'}`}>{item.description}</p>
          </div>

          {/* Actions — hidden on mobile, shown inline on sm+ */}
          <div className="hidden sm:flex gap-2 flex-shrink-0 items-center">
            {item.actionLink && (
              <button
                onClick={() => onNavigate(item.actionLink!)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
              >
                View
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
            {item.rawTask && item.rawTask.status !== "completed" && item.rawTask.status !== "skipped" && onCompleteTask && onSkipTask && (
              <>
                <button
                  onClick={() => onCompleteTask(item.rawTask!.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Done
                </button>
                <button
                  onClick={() => onSkipTask(item.rawTask!.id)}
                  className="px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Skip
                </button>
              </>
            )}
            {item.rawAlert && item.rawAlert.status !== "resolved" && onResolveAlert && (
              <button
                onClick={() => onResolveAlert(item.rawAlert!.id)}
                className="px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
              >
                Resolve
              </button>
            )}
            {hasInsightMenu && (
              <InsightMenu
                insightId={item.rawInsight!.id}
                onSnooze={onSnoozeInsight!}
                onResolve={onResolveInsight!}
                onSuppress={onSuppressInsight!}
              />
            )}
          </div>
        </div>

        {/* Mobile actions — shown below content on small screens */}
        <div className="flex gap-2 flex-wrap mt-2 ml-6 sm:hidden">
          {item.actionLink && (
            <button
              onClick={() => onNavigate(item.actionLink!)}
              className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
            >
              View
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
          {item.rawTask && item.rawTask.status !== "completed" && item.rawTask.status !== "skipped" && onCompleteTask && onSkipTask && (
            <>
              <button
                onClick={() => onCompleteTask(item.rawTask!.id)}
                className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Done
              </button>
              <button
                onClick={() => onSkipTask(item.rawTask!.id)}
                className="px-2.5 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Skip
              </button>
            </>
          )}
          {item.rawAlert && item.rawAlert.status !== "resolved" && onResolveAlert && (
            <button
              onClick={() => onResolveAlert(item.rawAlert!.id)}
              className="px-2.5 py-1 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
            >
              Resolve
            </button>
          )}
          {hasInsightMenu && (
            <InsightMenu
              insightId={item.rawInsight!.id}
              onSnooze={onSnoozeInsight!}
              onResolve={onResolveInsight!}
              onSuppress={onSuppressInsight!}
            />
          )}
        </div>

        {/* Expanded: Auth rates + correlation + recommendations */}
        {expanded && (
          <div className="mt-4 ml-7 space-y-3">
            {item.source === "insight" && item.metadata && (
              <InsightDetails metadata={item.metadata} />
            )}

            {item.recommendations.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Recommendations
                </p>
                <ul className="space-y-2">
                  {item.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-4 mt-2 ml-7 text-xs text-slate-400">
          <span>{new Date(item.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}
